/**
 * Verifying the server's key log instead of believing it.
 *
 * TOFU (chatTrust.mjs) catches a key that CHANGES under somebody you already
 * know. It is blind to two things a compromised server can do without a single
 * screen changing: add a device for a person you have not written to yet, and
 * remove a device that was there yesterday. Both look like ordinary life.
 *
 * The server therefore keeps an append-only chain of every identity key it was
 * ever told about, each entry carrying the hash of the head before it. This
 * module walks that chain forward from the head we last saw and recomputes
 * every link. The server can still APPEND — it always could — but it can no
 * longer rewrite: any change to an old entry moves every head after it, and
 * the walk stops on the first link that does not add up.
 *
 * What this does NOT catch, and it matters: a server that keeps two logs and
 * shows a different one to each person. Every chain verifies perfectly on its
 * own. Signal rules that out with an independent auditor; without one, the job
 * belongs to clients comparing heads with each other over the sessions they
 * already have — the gossip stage in docs/plans/key-transparency.md. Until
 * then this detects rewriting and not split views, and the difference should
 * not be blurred.
 */

const GENESIS = 'voxiox-key-log-v1';
const SEP = '\x1f';

const enc = new TextEncoder();

/** head = SHA-256(prev_head || 0x1f || fields…), hex — the same recipe as the server. */
export async function headFor(prevHead, entry) {
  const parts = [prevHead];
  for (const p of [String(entry.seq), String(entry.user_id), entry.device_id,
                   entry.identity_sig, entry.identity_dh, entry.action]) {
    parts.push(SEP, p);
  }
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(parts.join('')));
  return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
}

export const KEY_LOG_ERROR = {
  BROKEN_LINK: 'broken-link',       // an entry does not hash to the head that follows it
  FORKED: 'forked',                 // the chain does not continue from the head we remember
  GAP: 'gap',                       // sequence numbers skip — entries went missing
  HEAD_MISMATCH: 'head-mismatch',   // the server's claimed head is not where its own entries lead
};

/**
 * Walk a page of the log forward from what we last saw.
 *
 * `state` is `{ seq, head }` — the last entry we verified. A fresh client
 * starts at `{ seq: 0, head: GENESIS }`, which is trust on first use applied to
 * the log itself: we cannot audit what happened before we existed, and saying
 * so is better than pretending.
 */
export async function verifyLog(state, page) {
  const seen = state && state.head ? state : { seq: 0, head: GENESIS };
  const entries = (page && page.entries) || [];
  let { seq, head } = seen;

  for (const e of entries) {
    if (e.seq !== seq + 1) {
      // Either the server skipped entries or we asked from the wrong place.
      // Both mean the same thing here: we cannot vouch for what is between.
      return { ok: false, error: KEY_LOG_ERROR.GAP, at: e.seq, expected: seq + 1, state: seen };
    }
    if (e.prev_head !== head) {
      return { ok: false, error: KEY_LOG_ERROR.FORKED, at: e.seq, state: seen };
    }
    const computed = await headFor(head, e);
    if (computed !== e.head) {
      return { ok: false, error: KEY_LOG_ERROR.BROKEN_LINK, at: e.seq, state: seen };
    }
    seq = e.seq;
    head = e.head;
  }

  // The head the server claims must be the one its own entries produce —
  // otherwise it is holding something back.
  if (page && page.head && !page.hasMore && page.head !== head) {
    return { ok: false, error: KEY_LOG_ERROR.HEAD_MISMATCH, at: seq, state: seen };
  }

  return { ok: true, state: { seq, head }, added: entries };
}

/**
 * What the log says about one device, from the entries we have verified.
 *
 * `add` then `revoke` is a device that existed and was retired; a revoke with
 * no add before it is the log contradicting itself and is reported as unknown
 * rather than quietly treated as either.
 */
export function deviceHistory(entries, deviceId) {
  const mine = entries.filter(e => e.device_id === deviceId);
  if (!mine.length) return null;
  const added = mine.find(e => e.action === 'add');
  const revoked = mine.filter(e => e.action === 'revoke').pop();
  if (!added) return null;
  return {
    deviceId,
    userId: added.user_id,
    identitySig: added.identity_sig,
    addedAt: added.created_at,
    label: added.device_label || null,
    revokedAt: revoked ? revoked.created_at : null,
    seq: added.seq,
  };
}

/**
 * Does the bundle the server just handed us match what its own log recorded?
 *
 * This is the whole point of the log at the moment a session is built. A
 * bundle whose identity key is not the one logged for that device is the
 * server saying two different things, in writing.
 */
export function bundleAgreesWithLog(entries, bundle) {
  const h = deviceHistory(entries, bundle.deviceId);
  if (!h) return { known: false };
  return {
    known: true,
    agrees: h.identitySig === bundle.identitySig,
    revoked: !!h.revokedAt,
    addedAt: h.addedAt,
    label: h.label,
  };
}

export { GENESIS };
